import {useState} from 'react';
import Image from 'next/image';
import {useRouter} from 'next/navigation';
import {CartItem} from '@delorand/db/schema/customer';
import Button from '@delorand/ui/src/button';
import Checkbox from '@delorand/ui/src/checkbox';
import {LoadingBlur} from '@delorand/ui/src/loader';
import {TrashSvg} from '@delorand/ui/src/svg/shared';
import {cn} from '@delorand/utils/cn';
import {sumBy} from '@delorand/utils/lod';
import {formatCurrency, getThumbnail} from '@delorand/utils/shared';
import {CheckIcon} from '@heroicons/react/24/outline';
import ItemsGrid from 'c/components/item/Items-grid';
import {useCart, useModifyCart} from 'c/utils/cart';
import {useUser} from 'c/utils/hooks';
import useSWRMutation from 'swr/mutation';
import {useStore} from '../../../store/store';
import {client} from '../../../utils/api';
import {fetchApi} from '../../../utils/misc';

const CartComponent = () => {
  const router = useRouter();

  const [userdata] = useUser();
  const {cartItems, isLoading} = useCart();

  const [selected, setSelected] = useState<number[]>([]);

  const {updateCart} = useModifyCart();

  const selectedItems = cartItems.filter(cartItem =>
    selected.some(x => x === cartItem.id)
  );

  const subTotal = sumBy(
    selectedItems.map(item => item.variant.price * item.quantity)
  );

  const defaultAddress = userdata?.shippingAddresses.find(x => x.default);

  const selectedLocation = useStore(state => state.location);

  const location = selectedLocation ?? {
    cityId: defaultAddress?.cityId,
    stateId: defaultAddress?.stateId,
  };

  const {
    trigger: checkout,
    isMutating,
    error,
  } = useSWRMutation(
    ['checkout'],
    () =>
      fetchApi(
        client.order.checkout.$post({
          json: {
            items: selectedItems.map(({variantId, quantity}) => ({
              variantId,
              quantity,
            })),
            storesCarriers: [],
            cityId: location.cityId,
            stateId: location.stateId,
          },
        })
      ),
    {
      onSuccess: () => {
        router.push(`/checkout?ts=${Date.now()}`);
      },
    }
  );

  return (
    <>
      {isLoading && <LoadingBlur />}
      <div className="mt-3 flex justify-between px-6 text-[15px]">
        <button
          aria-label="check-all"
          className={`my-2 flex items-center gap-2 text-sm font-medium disabled:opacity-50`}
          disabled={cartItems.length < 1}
          onClick={() =>
            cartItems.length === selected.length
              ? setSelected([])
              : setSelected(cartItems.map(item => item.id) || [])
          }
        >
          <span>Select all</span>
          <CheckIcon width={20} strokeWidth={1.5} stroke="white" />
        </button>
        <button
          className="flex items-center gap-2 text-sm font-medium disabled:opacity-50"
          aria-label="delete-all-cart"
          disabled={!!(selected.length < 1)}
          onClick={() => {
            updateCart({
              remove: {
                type: 'ALL',
                variantIds: [],
              },
            });
            setSelected([]);
          }}
        >
          <span>Clear all</span>
          <TrashSvg />
        </button>
      </div>

      <div
        className={`mt-4 w-full rounded-xl px-3 ${cartItems.length < 1 && 'opacity-40'}`}
      >
        {cartItems.length < 1 && (
          <p className="pt-5 text-center text-lg opacity-50">Empty</p>
        )}
        <div className="grid grid-cols-1 gap-3">
          {cartItems.map(item => (
            <CartItemComponent
              removeItem={() => {
                setSelected(old => old.filter(itemId => itemId !== item.id));
                updateCart({
                  remove: {
                    type: 'ONE',
                    variantIds: [item.variantId],
                  },
                });
              }}
              selected={selected.includes(item.id)}
              handleSelect={() =>
                setSelected(old =>
                  old.includes(item.id)
                    ? old.filter(x => x !== item.id)
                    : [...old, item.id]
                )
              }
              key={item.id}
              item={item as any}
            />
          ))}
        </div>
      </div>

      {/* cart summary */}

      <div
        className={`mx-auto mt-14 flex h-fit w-[94%] flex-col gap-2 rounded-lg bg-white/10 px-3 pb-5 ${selected.length < 1 && 'opacity-50'}`}
      >
        <p className="my-3 border-b border-b-white/10 pb-1 text-center text-lg font-semibold">
          Cart summary
        </p>
        <div className="flex justify-between px-3 pt-2">
          <p className="">Cart Total</p>
          <p>{formatCurrency(subTotal)}</p>
        </div>
        <div className="flex justify-between px-3 py-1">
          <p className="">Items</p>
          <p className="">{selected?.length}</p>
        </div>
        <p
          className={`mb-4 ml-5 text-sm ${cartItems?.length > 0 ? '' : 'invisible'}`}
        >
          Delivery fees not included yet.
        </p>

        {error && <p className="error">{error?.message}</p>}

        <Button
          disabled={selected.length < 1}
          isLoading={isMutating}
          onClick={() => {
            if (!userdata) router.push('/login?callbackUrl=/checkout');
            else checkout();
          }}
        >
          Checkout
        </Button>
      </div>

      <ItemsGrid limit={10}>
        <p className="mt-10 px-3 text-center text-lg font-medium">
          More items to love
        </p>
      </ItemsGrid>
    </>
  );
};

export default CartComponent;

const CartItemComponent = ({
  item,
  selected,
  handleSelect,
  removeItem,
}: {
  item: CartItem;
  selected: boolean;
  handleSelect: () => void;
  removeItem: () => void;
}) => {
  const router = useRouter();

  const {updateCart, isUpdatingCart} = useModifyCart();

  return (
    <div
      key={`${item.id}`}
      className={cn(
        'relative flex w-full flex-col gap-3 rounded-lg bg-white/10 px-2 py-4 md:gap-4 md:p-3',
        item.variant.stock < 1 ? 'opacity-80' : ''
      )}
    >
      {isUpdatingCart && <LoadingBlur />}
      <div className="absolute top-4 right-3">
        <Checkbox
          disabled={item.variant.stock < 1}
          checked={selected}
          handleChange={handleSelect}
        />
      </div>
      <div className="flex gap-2">
        <div className="w-1/3">
          <Image
            alt={
              item.variant.product.title +
              '-' +
              item.variant.attributes
                .map(x => x.option)
                .join('-')
                .slice(0, 10)
            }
            src={getThumbnail(item.variant.product.images)}
            width={300}
            height={300}
            className="w-full rounded-lg bg-black/30 object-contain group-hover:opacity-60"
            onClick={() => router.push(`/item/${item.variant.product.slug}`)}
          />
        </div>
        <div className="flex w-2/3 flex-col gap-1.5 px-5 py-1 leading-5">
          <h3 className="w-4/5 flex-1 text-[15px] break-words md:text-lg">
            {item.variant.product.title}
          </h3>
          <p className="text-custom-neutral text-sm">
            {item.variant.attributes
              .map(x => x.option)
              .join('-')
              .slice(0, 10)}
          </p>

          <p className="whitespace-nowrap">
            {formatCurrency(item.variant.price)}
          </p>

          <p className="text-sm text-[#77797C]">
            {item.variant.stock > 1 ? 'In Stock' : 'No Stock'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between px-3">
        <button
          className="text-custom-neutral flex items-center gap-3"
          aria-label="delete-item-from-cart"
          onClick={removeItem}
        >
          <TrashSvg />
          <span>REMOVE</span>
        </button>

        <div className="flex w-36 items-center justify-between gap-y-2 font-bold">
          <button
            className="bg-deep-black flex h-10 w-10 items-center justify-center rounded-full disabled:opacity-50"
            aria-label="decreament-item"
            disabled={item.quantity < 2}
            onClick={() => {
              updateCart({
                update: {
                  type: 'DECR',
                  variantId: item.variant.id,
                },
              });
            }}
          >
            <span className="text-3xl">-</span>
          </button>

          <div className="flex items-center justify-center text-xl">
            <p>{item.quantity}</p>
          </div>

          <button
            className="bg-deep-black flex h-10 w-10 items-center justify-center rounded-full disabled:opacity-50"
            aria-label="decreament-item"
            disabled={item.quantity > 19 || item.variant.stock < item.quantity}
            onClick={() => {
              updateCart({
                update: {
                  type: 'INCR',
                  variantId: item.variant.id,
                },
              });
            }}
          >
            <span className="text-3xl">+</span>
          </button>
        </div>
      </div>
    </div>
  );
};
